import { useCallback } from 'react';
import {
  DailyVideo,
  useLocalSessionId,
  useParticipantIds,
  useParticipantProperty,
  useScreenShare,
} from '@daily-co/daily-react';
import { useSubjectLanguageVariants } from '@features/user';
import { PipIcon } from '@icons/PipIcon';
import {
  ArrowBack as ArrowBackIcon,
  ChatBubbleOutline as ChatIcon,
  FiberManualRecord as RecordIcon,
  Logout as LogoutIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
} from '@mui/icons-material';
import { Badge, Box, IconButton, Stack, Typography } from '@mui/material';
import type { DailyCallService } from '@services/dailycall';
import { useDailyCallState } from '@services/dailycall';
import { useAppSelector } from '@store/hooks';

import { selectChatUnreadCount } from '../../store';
import { WaitingParticipants } from '../WaitingParticipants';

interface CompactPipViewProps {
  dailyCallService: DailyCallService;
  isRecording: boolean;
  isRecordingPaused?: boolean;
  isOwner: boolean;
  onLeave: () => void;
  onClose: () => void;
  onEnterPip?: () => void;
  isPipSupported?: boolean;
  onOpenChat?: () => void;
}

function RecordingIndicator({ isRecording, isPaused }: { isRecording: boolean; isPaused: boolean }) {
  if (!isRecording && !isPaused) return null;

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <RecordIcon
        sx={{
          fontSize: 10,
          color: isPaused ? 'warning.main' : 'error.main',
          animation: isPaused ? 'none' : 'pulse 1.5s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.3 },
          },
        }}
      />
      <Typography variant="caption" sx={{ fontSize: 10, color: isPaused ? 'warning.main' : 'error.main' }}>
        {isPaused ? 'PAUSED' : 'REC'}
      </Typography>
    </Stack>
  );
}

function RemoteVideoArea({ remoteId }: { remoteId: string | undefined }) {
  const { singular } = useSubjectLanguageVariants();
  const remoteVideo = useParticipantProperty(remoteId ?? '', 'video');
  const remoteName = useParticipantProperty(remoteId ?? '', 'user_name');
  const { screens, isSharingScreen } = useScreenShare();

  // Show remote screen share instead of camera when someone else is sharing
  const hasRemoteScreenShare = screens.length > 0 && !isSharingScreen;
  const screenShareSessionId = hasRemoteScreenShare ? screens[0].session_id : null;

  if (!remoteId) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography variant="body2" sx={{ color: 'grey.400' }}>
          {`Waiting for ${singular}...`}
        </Typography>
      </Box>
    );
  }

  // Remote screen share active — show shared screen
  if (hasRemoteScreenShare && screenShareSessionId) {
    return (
      <DailyVideo
        sessionId={screenShareSessionId}
        type="screenVideo"
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
    );
  }

  // Remote camera
  if (remoteVideo) {
    return (
      <DailyVideo
        sessionId={remoteId}
        type="video"
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
    );
  }

  // Camera off — show name
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
      }}
    >
      <Typography variant="body1" sx={{ color: 'common.white', fontWeight: 500 }}>
        {remoteName || 'Guest'}
      </Typography>
    </Box>
  );
}

export function CompactPipView({
  dailyCallService,
  isRecording,
  isRecordingPaused = false,
  isOwner,
  onLeave,
  onClose,
  onEnterPip,
  isPipSupported = false,
  onOpenChat,
}: CompactPipViewProps) {
  const callState = useDailyCallState(dailyCallService);
  const remoteIds = useParticipantIds({ filter: 'remote' });
  const localSessionId = useLocalSessionId();
  const chatUnreadCount = useAppSelector(selectChatUnreadCount);

  const handleToggleMic = useCallback(() => {
    dailyCallService.setLocalAudio(!callState.localAudioEnabled);
  }, [callState.localAudioEnabled, dailyCallService]);

  const handleToggleVideo = useCallback(() => {
    dailyCallService.setLocalVideo(!callState.localVideoEnabled);
  }, [callState.localVideoEnabled, dailyCallService]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        bgcolor: 'grey.900',
        overflow: 'hidden',
      }}
    >
      {/* Waiting participants — admit/deny only (no invite links in compact) */}
      <WaitingParticipants isOwner={isOwner} hasRemoteParticipants={remoteIds.length > 0} />

      {/* Header: recording indicator + close */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1,
          py: 0.5,
          zIndex: 2,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <IconButton onClick={onClose} size="small" sx={{ color: 'common.white', p: 0.5 }} aria-label="Return to encounter">
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <RecordingIndicator isRecording={isRecording} isPaused={isRecordingPaused} />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {chatUnreadCount > 0 && onOpenChat && (
            <IconButton onClick={onOpenChat} size="small" sx={{ color: 'common.white', p: 0.5 }} aria-label="Open chat">
              <Badge badgeContent={chatUnreadCount} color="error" max={99}>
                <ChatIcon sx={{ fontSize: 18 }} />
              </Badge>
            </IconButton>
          )}
          {isPipSupported && onEnterPip && (
            <IconButton onClick={onEnterPip} size="small" sx={{ color: 'common.white', p: 0.5 }} aria-label="Picture-in-picture">
              <PipIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* Video area */}
      <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
        <RemoteVideoArea remoteId={remoteIds[0]} />

        {/* Local self-view thumbnail */}
        {localSessionId && callState.localVideoEnabled && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              width: 80,
              height: 60,
              borderRadius: 0.5,
              border: '1px solid',
              borderColor: 'common.white',
              overflow: 'hidden',
              bgcolor: 'grey.800',
            }}
          >
            <DailyVideo
              sessionId={localSessionId}
              type="video"
              mirror
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
        )}
      </Box>

      {/* Controls bar */}
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={1}
        sx={{ py: 0.75, px: 1, bgcolor: 'rgba(0,0,0,0.8)' }}
      >
        <IconButton
          onClick={handleToggleMic}
          size="small"
          sx={{ color: callState.localAudioEnabled ? 'common.white' : 'error.main' }}
          aria-label={callState.localAudioEnabled ? 'Mute microphone' : 'Unmute microphone'}
        >
          {callState.localAudioEnabled ? <MicIcon sx={{ fontSize: 20 }} /> : <MicOffIcon sx={{ fontSize: 20 }} />}
        </IconButton>

        <IconButton
          onClick={handleToggleVideo}
          size="small"
          sx={{ color: callState.localVideoEnabled ? 'common.white' : 'error.main' }}
          aria-label={callState.localVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
        >
          {callState.localVideoEnabled ? (
            <VideocamIcon sx={{ fontSize: 20 }} />
          ) : (
            <VideocamOffIcon sx={{ fontSize: 20 }} />
          )}
        </IconButton>

        <IconButton
          onClick={onLeave}
          size="small"
          sx={{ color: 'error.main' }}
          aria-label="Leave call"
        >
          <LogoutIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Stack>
    </Box>
  );
}
